/*** IMPORTS FROM imports-loader ***/
import "breeze.dataService.webApi";

import { JsonResultsAdapter, EntityQuery, MetadataStore, core, Validator, DataType, config, AutoGeneratedKeyType, DataProperty, EntityType, ComplexType, Predicate } from 'breeze-client';
import { oData } from 'ts-odatajs';

class JsonResultsAdapterFactory {
    static create() {
        const adapter = new JsonResultsAdapter({
            name: 'OData4',
            visitNode: JsonResultsAdapterFactory.visitNode
        });
        return adapter;
    }
    static visitNode(node, mappingContext, nodeContext) {
        const result = {};
        const metadataStore = mappingContext.entityManager.metadataStore;
        const workingNode = node;
        let nodeODataType;
        if (node === null) {
            return result;
        }
        let entityType;
        let entityTypeName;
        if (nodeContext.nodeType === 'root') {
            if (mappingContext.query instanceof EntityQuery) {
                const eq = mappingContext.query;
                if (eq.resultEntityType) {
                    entityType = eq.resultEntityType;
                }
                else {
                    entityTypeName = metadataStore.getEntityTypeNameForResourceName(eq.resourceName);
                }
            }
            else {
                nodeODataType = node['@odata.type'];
                entityTypeName = MetadataStore.normalizeTypeName(nodeODataType.replace('#', ''));
            }
        }
        else if (nodeContext.nodeType === 'navProp' || nodeContext.nodeType === 'navPropItem') {
            entityTypeName = nodeContext.navigationProperty.entityTypeName;
        }
        entityType = entityType || (entityTypeName && metadataStore.getEntityType(entityTypeName, true));
        if (entityType) {
            result.entityType = entityType;
            result.extraMetadata = {};
        }
        if (workingNode.results) {
            result.node = workingNode.results;
        }
        const propertyName = nodeContext.propertyName;
        result.ignore =
            workingNode.__deferred != null ||
                propertyName === '__metadata' ||
                (propertyName === 'EntityKey' && workingNode.$type && core.stringStartsWith(workingNode.$type, 'System.Data'));
        return result;
    }
}

class AssociationEndpoint {
    constructor(init) {
        this.ConstrainMany = '*';
        this.ConstrainOne = '1';
        this.referentialConstraint = [];
        Object.assign(this, init);
    }
    get containingEntityShortName() {
        const result = this.containingEntityType.split('.').pop();
        return result;
    }
    get isCollection() {
        const result = !!this.navigationProperty
            && oData.utils.isCollectionType(this.navigationProperty.type);
        return result;
    }
    get isMapped() {
        const result = !!this.navigationProperty;
        return result;
    }
    get multiplicity() {
        const result = this.isCollection ? this.ConstrainMany : this.ConstrainOne;
        return result;
    }
    get order() {
        const result = Number(!this.isMapped) * 2 + Number(this.isCollection);
        return result;
    }
    get partnerEntityShortName() {
        const result = this.partnerEntityType.split('.').pop();
        return result;
    }
    get propertyName() {
        const result = this.isMapped ? this.navigationProperty.name : this._propertyName;
        return result;
    }
    set propertyName(value) {
        this._propertyName = value;
    }
    get role() {
        const result = `${this.containingEntityShortName}_${this.propertyName}`;
        return result;
    }
}

class AssociationSet {
    constructor(namespace, endpoint, partnerEndpoint) {
        this.namespace = namespace;
        this.endpoints = [endpoint, partnerEndpoint];
    }
    get associationName() {
        const result = `${this.namespace}.${this.name}`;
        return result;
    }
    containsProperty(navigationProperty) {
        const result = !!navigationProperty
            && this.endpoints.some(e => e.navigationProperty === navigationProperty);
        return result;
    }
    get endpoints() {
        return this._endpoints;
    }
    set endpoints(value) {
        this._endpoints = value.sort((a, b) => a.order - b.order);
    }
    getPartnerEndpoint(endpoint) {
        const result = endpoint && this.endpoints.find(e => e !== endpoint);
        return result;
    }
    get fullyMapped() {
        const result = this.endpoints.every(e => e.isMapped);
        return result;
    }
    get name() {
        const result = this.endpoints
            .map(e => e.role)
            .join('_');
        return result;
    }
}

class EdmEnum extends core.Enum {
    constructor(options) {
        super(options.name);
        this.validatorCtor = Validator.none;
        this.members = [];
        this.parse = (val, sourceTypeName) => {
            if (typeof val === 'undefined' || val === null) {
                return null;
            }
            if (sourceTypeName === 'object' && val['parentEnum'] === this) {
                return val;
            }
            if (sourceTypeName === 'string') {
                return this.fromName(val);
            }
            if (sourceTypeName === 'number') {
                return this.fromValue(val);
            }
            return null;
        };
        this.parseRawValue = (val) => {
            return this.parse(val, 'string');
        };
        this.isFlags = options.isFlags;
        this.name = options.name;
        this.underlyingDataType = options.underlyingDataType;
    }
    addSymbol(enumMember) {
        var _a;
        (_a = enumMember.displayName) !== null && _a !== void 0 ? _a : (enumMember.displayName = enumMember.name);
        enumMember.value = this.underlyingDataType.parse(enumMember.rawValue, 'string');
        const result = super.addSymbol(enumMember);
        const memberResult = result;
        this.members.push(memberResult);
        if (!this.defaultValue) {
            this.defaultValue = memberResult;
        }
        this[enumMember.name] = result;
        return result;
    }
    fromValue(value) {
        let result;
        if (this.isFlags && value) {
            result = this.fromFlagsEnumValue(value);
        }
        else {
            result = this.members.find(m => m.value === value);
        }
        return result;
    }
    fromName(name) {
        const value = Number(name);
        if (!isNaN(value)) {
            return this.fromValue(value);
        }
        const result = this.isFlags && name.includes(',')
            ? this.fromFlagsEnumName(name)
            : super.fromName(name);
        return result;
    }
    fromFlagsEnumValue(value) {
        const result = this.createEdmEnumMember();
        const flags = this.members
            .filter(m => m.value > 0 && (m.value | value) === value);
        if (!flags.length) {
            return undefined;
        }
        result.displayName = flags.map(f => f.displayName).join(', ');
        result.name = flags.map(f => f.name).join(',');
        result.value = flags.reduce((tot, cur) => tot + cur.value, 0);
        return result;
    }
    fromFlagsEnumName(name) {
        let result;
        name.split(',')
            .filter(n => this.members.some(m => m.name === n))
            .forEach(memberName => {
            const symbol = super.fromName(memberName.trim());
            if (!result) {
                result = Object.assign(this.createEdmEnumMember(), symbol);
                result.name = name;
            }
            else {
                result.displayName += `, ${symbol.displayName}`;
                result.value += symbol.value;
            }
        }, 0);
        return result;
    }
    createEdmEnumMember() {
        const result = Object.create(this._symbolPrototype);
        return result;
    }
}

const EntityNotFound = 'Could not find entity with type name';
const PartnerSuffix = 'Partner';
class NavigationAdapter {
    constructor() {
        this.associations = [];
    }
    adapt(metadata) {
        this.metadata = metadata;
        this.entityContainer = oData.utils.lookupDefaultEntityContainer(this.metadata.schema);
        oData.utils.forEachSchema(this.metadata.schema, this.adaptSchema.bind(this));
    }
    adaptSchema(schema) {
        this.associations = [];
        const entityTypes = schema.entityType || [];
        entityTypes.forEach(e => this.adaptEntityType(schema, e));
        this.setAssociations(schema);
    }
    adaptEntityType(schema, entityType) {
        (entityType.navigationProperty || []).forEach(n => this.adaptNavigationProperty(schema, entityType.name, n));
    }
    adaptNavigationProperty(schema, entityTypeName, navigationProperty) {
        const namespace = schema.namespace;
        const navPropType = this.getUnderlyingEntityType(navigationProperty);
        const endpoint = new AssociationEndpoint({
            containingEntityType: `${namespace}.${entityTypeName}`,
            partnerEntityType: navPropType,
            navigationProperty: navigationProperty
        });
        this.trySetReferentialConstraint(endpoint);
        let partnerNavProp = this.tryGetPartnerNavigationProperty(endpoint);
        if (!this.validateRelationship(navigationProperty, partnerNavProp)) {
            partnerNavProp = null;
        }
        const partnerEndpoint = new AssociationEndpoint({
            containingEntityType: navPropType,
            partnerEntityType: `${namespace}.${entityTypeName}`,
            navigationProperty: partnerNavProp,
            propertyName: !!partnerNavProp ? null : `${navigationProperty.name}${PartnerSuffix}`
        });
        this.trySetReferentialConstraint(partnerEndpoint);
        this.setAssociationSet(namespace, endpoint, partnerEndpoint);
    }
    getUnderlyingEntityType(navigationProperty) {
        const result = oData.utils.getCollectionType(navigationProperty.type) || navigationProperty.type;
        return result;
    }
    trySetReferentialConstraint(endpoint) {
        if (endpoint.isMapped && endpoint.navigationProperty.referentialConstraint) {
            endpoint.referentialConstraint = endpoint.navigationProperty.referentialConstraint;
            return;
        }
        if (endpoint.isCollection || !NavigationAdapter.inferConstraints) {
            return;
        }
        const entityType = oData.utils.lookupEntityType(endpoint.containingEntityType, this.metadata.schema);
        const partnerEntityType = oData.utils.lookupEntityType(endpoint.partnerEntityType, this.metadata.schema);
        if (partnerEntityType === null) {
            throw new Error(`${EntityNotFound} ${endpoint.partnerEntityType}`);
        }
        const entityName = endpoint.containingEntityShortName;
        const partnerEntityName = endpoint.partnerEntityShortName;
        const referentialConstraint = [];
        const entityKeys = this.getKeys(entityType);
        const entityProperties = this.getProperties(entityType);
        const partnerKeys = this.getKeys(partnerEntityType);
        const partnerProperties = this.getProperties(partnerEntityType);
        partnerKeys.forEach(r => {
            const partnerKeyProp = partnerProperties.find(p => p.name === r.name);
            const keySuffix = r.name.replace(partnerEntityName, '');
            let possibleFKs;
            if (r.name.toLowerCase() === `${entityName}Id`.toLowerCase()) {
                possibleFKs = entityKeys.map(p => p.name.toLowerCase());
            }
            else {
                possibleFKs = NavigationAdapter.foreignKeyConventions
                    .map(func => func(endpoint, keySuffix))
                    .filter(p => !referentialConstraint.find(rc => rc.property.toLowerCase() === p));
            }
            const propsMatchingType = entityProperties.filter(p => p.type === partnerKeyProp.type);
            const fkProp = possibleFKs
                .map(fk => propsMatchingType.find(p => p.name.toLowerCase() === fk))
                .find(p => !!p);
            if (fkProp) {
                referentialConstraint.push({ property: fkProp.name, referencedProperty: r.name });
            }
        });
        endpoint.referentialConstraint = referentialConstraint;
    }
    getAllTypes(entityType) {
        const result = [entityType];
        let currentType = entityType;
        while (!!currentType.baseType) {
            const baseType = currentType.baseType;
            currentType = oData.utils.lookupEntityType(currentType.baseType, this.metadata.schema);
            if (currentType === null) {
                throw new Error(`${EntityNotFound} ${baseType}`);
            }
            result.push(currentType);
        }
        return result;
    }
    getKeys(entityType) {
        const types = this.getAllTypes(entityType);
        const result = types
            .reduce((a, b) => a.concat((b.key && b.key.propertyRef) || []), []);
        return result;
    }
    getNavigationProperties(entityType) {
        const types = this.getAllTypes(entityType);
        const result = types
            .reduce((a, b) => a.concat(b.navigationProperty || []), []);
        return result;
    }
    getProperties(entityType) {
        const types = this.getAllTypes(entityType);
        const result = types
            .reduce((a, b) => a.concat(b.property || []), []);
        return result;
    }
    tryGetPartnerNavigationProperty(endpoint) {
        const navProp = endpoint.navigationProperty;
        const navPropType = this.getUnderlyingEntityType(navProp);
        const partnerEntityType = oData.utils.lookupEntityType(navPropType, this.metadata.schema);
        if (partnerEntityType === null) {
            throw new Error(`${EntityNotFound} ${navPropType}`);
        }
        const partnerNameCandidates = [];
        if (navProp.partner) {
            partnerNameCandidates.push(navProp.partner);
        }
        else if (NavigationAdapter.inferPartner) {
            const entitySetName = this.getEntitySetNameByEntityType(endpoint.containingEntityType);
            partnerNameCandidates.push(endpoint.containingEntityShortName);
            partnerNameCandidates.push(entitySetName);
        }
        const partnerNavProperties = this.getNavigationProperties(partnerEntityType);
        let partnerNavProp = partnerNavProperties
            .find(n => n !== navProp
            && partnerNameCandidates.includes(n.name)
            && (!n.partner || n.partner === navProp.name));
        if (!!partnerNavProp || !NavigationAdapter.inferPartner) {
            return partnerNavProp;
        }
        partnerNavProp = partnerNavProperties
            .find(n => n !== navProp
            && this.navigationPropertyTypeMatches(n, endpoint.containingEntityType)
            && (!n.partner || n.partner === navProp.name));
        return partnerNavProp;
    }
    validateRelationship(...navigationProperties) {
        const result = NavigationAdapter.allowManyToMany
            || !navigationProperties.every(n => !!n && oData.utils.isCollectionType(n.type));
        return result;
    }
    getEntitySetNameByEntityType(entityType) {
        const set = this.entityContainer.entitySet.find(s => s.entityType === entityType);
        return set && set.name;
    }
    navigationPropertyTypeMatches(navigationProperty, type) {
        const navPropType = this.getUnderlyingEntityType(navigationProperty);
        const result = navPropType === type;
        return result;
    }
    setAssociationSet(namespace, endpoint, partnerEndpoint) {
        const assoc = this.associations.find(a => (a.containsProperty(partnerEndpoint.navigationProperty)
            || a.containsProperty(endpoint.navigationProperty)));
        const mappedEndpoints = [endpoint, partnerEndpoint].filter(e => assoc && assoc.containsProperty(e.navigationProperty));
        if (mappedEndpoints.length === 2) {
            return;
        }
        const newAssoc = new AssociationSet(namespace, endpoint, partnerEndpoint);
        if (assoc && !assoc.fullyMapped && newAssoc.fullyMapped) {
            assoc.endpoints = newAssoc.endpoints;
        }
        else if (!assoc || newAssoc.fullyMapped) {
            this.associations.push(newAssoc);
        }
    }
    setAssociations(schema) {
        const associations = this.associations.map(a => this.processAssociation(a));
        schema.association = associations;
        this.entityContainer.associationSet = associations;
    }
    getAssociationEndpoint(endpoint) {
        const result = {
            entitySet: this.getEntitySetNameByEntityType(endpoint.partnerEntityType),
            multiplicity: endpoint.multiplicity,
            role: endpoint.role,
            type: endpoint.partnerEntityType
        };
        return result;
    }
    processAssociation(association) {
        const result = {
            association: association.associationName,
            name: association.name,
            end: association.endpoints.map(e => this.getAssociationEndpoint(e)),
            referentialConstraint: this.getReferentialConstraint(association)
        };
        association.endpoints.forEach(e => {
            if (!e.isMapped) {
                return;
            }
            const partnerEndpoint = association.getPartnerEndpoint(e);
            const navProp = e.navigationProperty;
            if (!!navProp.relationship) {
                return;
            }
            navProp.relationship = association.associationName;
            navProp.fromRole = partnerEndpoint.role;
            navProp.toRole = e.role;
        });
        return result;
    }
    getReferentialConstraint(association) {
        const constraintKeys = {};
        const result = {
            dependent: {
                propertyRef: null,
                role: null
            },
            principal: {
                propertyRef: null,
                role: null
            }
        };
        const principalEndpoint = association.endpoints[0].isCollection ? association.endpoints[1] : association.endpoints[0];
        const dependentEndpoint = association.getPartnerEndpoint(principalEndpoint);
        const constraint = principalEndpoint.referentialConstraint;
        if (constraint.length === 0) {
            return null;
        }
        result.principal.role = principalEndpoint.role;
        result.dependent.role = dependentEndpoint.role;
        constraintKeys[result.dependent.role] = constraint.map(p => ({ name: p.property }));
        constraintKeys[result.principal.role] = constraint.map(p => ({ name: p.referencedProperty }));
        Object.keys(result).forEach(p => {
            const member = result[p];
            member.propertyRef = constraintKeys[member.role];
        });
        return result;
    }
}
NavigationAdapter.allowManyToMany = false;
NavigationAdapter.inferConstraints = true;
NavigationAdapter.inferPartner = true;
NavigationAdapter.foreignKeyConventions = [
    (endpoint, suffix) => `${endpoint.propertyName}${suffix}`.toLowerCase(),
    (endpoint) => `${endpoint.propertyName}Id`.toLowerCase(),
    (endpoint, suffix) => `${endpoint.partnerEntityShortName}${suffix}`.toLowerCase(),
    (endpoint) => `${endpoint.partnerEntityShortName}Id`.toLowerCase()
];

class ClassRepository {
    constructor() {
        this.types = [];
    }
    add(...types) {
        this.types = this.types.concat(types);
    }
    get() {
        const results = this.types.map(t => new t());
        return results;
    }
}
class ClassRegistry {
}
ClassRegistry.AnnotationDecorators = new ClassRepository();
ClassRegistry.DataTypeSetups = new ClassRepository();
ClassRegistry.MetadataAdapters = new ClassRepository();

const TargetNotFound = 'Could not find element with type name';
class AnnotationAdapter {
    constructor() {
        this.decorators = [];
        this.edmTypes = {
            action: ['parameter'],
            complexType: ['property', 'navigationProperty'],
            entityContainer: ['actionImport', 'entitySet', 'functionImport', 'singleton'],
            entityType: ['property', 'navigationProperty'],
            enumType: ['member'],
            function: ['parameter'],
            term: [],
            typeDefinition: []
        };
        this.decorators = ClassRegistry.AnnotationDecorators.get();
    }
    adapt(metadata) {
        this.metadata = metadata;
        oData.utils.forEachSchema(this.metadata.schema, this.adaptSchema.bind(this));
    }
    adaptSchema(schema) {
        const annotations = schema.annotations || [];
        annotations.forEach((itemAnnotation) => {
            const annotatableType = this.getAnnotatableType(schema, itemAnnotation.target);
            if (annotatableType === null) {
                throw new Error(`${TargetNotFound} ${itemAnnotation.target}`);
            }
            const itemAnnotations = itemAnnotation.annotation || [];
            itemAnnotations.forEach((annotation) => {
                const decorator = this.decorators.find(d => d.canDecorate(annotation));
                if (!decorator) {
                    return;
                }
                decorator.decorate(annotatableType, annotation);
            });
        });
    }
    getAnnotatableType(schema, target) {
        const targetSplit = target.split('/');
        const typeName = targetSplit[0];
        const propertyName = targetSplit[1];
        let result = null;
        let kind;
        for (const edmType in this.edmTypes) {
            kind = edmType;
            const schemaProperty = schema[kind];
            if (!schemaProperty) {
                continue;
            }
            else if (schemaProperty instanceof Array) {
                result = oData.utils.lookupInMetadata(typeName, this.metadata.schema, kind);
            }
            else if (`${schema.namespace}.${schemaProperty.name}` === typeName) {
                result = schemaProperty;
            }
            if (result) {
                break;
            }
        }
        if (!propertyName) {
            return result;
        }
        const baseType = result;
        result = null;
        const properties = this.edmTypes[kind];
        properties.forEach(p => {
            if (result) {
                return;
            }
            let item;
            const typeProperty = baseType[p];
            if (!typeProperty) {
                return;
            }
            if (typeProperty instanceof Array) {
                item = typeProperty.find(e => e.name === propertyName);
            }
            else if (typeProperty.name === propertyName) {
                item = typeProperty;
            }
            if (item) {
                result = item;
            }
        });
        return result;
    }
}

const nameof = (name) => name;
var Utilities;
(function (Utilities) {
    Utilities.dataTypeMap = {
        bool: DataType.Boolean,
        float: DataType.Double,
        int: DataType.Int64,
        sbyte: DataType.Byte
    };
    function adaptMetadata(metadata) {
        if (metadata.version !== '4.0') {
            return metadata;
        }
        const metadataAdapters = ClassRegistry.MetadataAdapters.get();
        const csdlMetadata = metadata.dataServices;
        metadataAdapters.forEach(a => {
            oData.utils.forEachSchema(csdlMetadata, a.adapt.bind(a));
        });
        return metadata;
    }
    Utilities.adaptMetadata = adaptMetadata;
    function getDataType(key) {
        let result = Utilities.dataTypeMap[key];
        if (result) {
            return result;
        }
        const enumName = DataType.getNames().find(n => n.toLowerCase() === key.toLowerCase());
        result = !!enumName ? DataType[enumName] : DataType.String;
        return result;
    }
    Utilities.getDataType = getDataType;
    function getEdmTypeFromTypeName(metadata, typeName) {
        const bindingTypeName = oData.utils.getCollectionType(typeName) || typeName;
        const entityType = oData.utils.lookupEntityType(bindingTypeName, metadata);
        const complexType = oData.utils.lookupComplexType(bindingTypeName, metadata);
        return entityType || complexType;
    }
    Utilities.getEdmTypeFromTypeName = getEdmTypeFromTypeName;
    function adaptComplexType(metadataStore, complexType) {
        return adaptStructuralType(metadataStore, complexType);
    }
    Utilities.adaptComplexType = adaptComplexType;
    function adaptEntityType(metadataStore, entityType) {
        return adaptStructuralType(metadataStore, entityType);
    }
    Utilities.adaptEntityType = adaptEntityType;
    function adaptStructuralType(metadataStore, structuralType) {
        const result = metadataStore.getEntityType(structuralType.name, true);
        return result;
    }
    Utilities.adaptStructuralType = adaptStructuralType;
    function getActions(metadata, metadataStore) {
        const result = getInvokableEntries(metadata, metadataStore, s => s.action);
        return result;
    }
    Utilities.getActions = getActions;
    function getFunctions(metadata, metadataStore) {
        const result = getInvokableEntries(metadata, metadataStore, s => s.function);
        return result;
    }
    Utilities.getFunctions = getFunctions;
    function getInvokableEntries(metadata, metadataStore, accessor) {
        let result = [];
        metadata.dataServices.schema.forEach(s => {
            const namespace = s.namespace;
            const items = accessor(s);
            if (!items) {
                return;
            }
            const entries = items.map((config) => {
                const entry = {
                    config: config,
                    namespace: namespace,
                    url: getInvokableUrl(metadata, metadataStore, config, namespace)
                };
                return entry;
            });
            result = result.concat(entries);
        });
        return result;
    }
    Utilities.getInvokableEntries = getInvokableEntries;
    function getInvokableUrl(metadata, metadataStore, config, namespace) {
        const isBound = Boolean(config.isBound);
        let boundPart = '';
        if (isBound) {
            const bindingParameter = config.parameter[0];
            const bindingEdmType = getEdmTypeFromTypeName(metadata, bindingParameter.type);
            const breezeType = adaptEntityType(metadataStore, bindingEdmType);
            if (breezeType) {
                boundPart = `${breezeType.defaultResourceName}/`;
            }
        }
        const url = `${boundPart}${namespace}.${config.name}`;
        return url;
    }
    Utilities.getInvokableUrl = getInvokableUrl;
    function parseValue(dataType, value) {
        const result = dataType.parseRawValue ? dataType.parseRawValue(value) : dataType.parse(value, 'string');
        return result;
    }
    Utilities.parseValue = parseValue;
})(Utilities || (Utilities = {}));

const DefaultIsFlags = 'false';
const DefaultUnderlyingType = 'Edm.Int32';
const TrueValue = 'true';
class EnumTypeAdapter {
    adapt(metadata) {
        oData.utils.forEachSchema(metadata.schema, this.adaptSchema.bind(this));
    }
    adaptSchema(schema) {
        const enumTypes = schema.enumType || [];
        enumTypes.forEach(this.adaptEnumType, this);
    }
    adaptEnumType(enumType) {
        var _a, _b;
        (_a = enumType.isFlags) !== null && _a !== void 0 ? _a : (enumType.isFlags = DefaultIsFlags);
        (_b = enumType.underlyingType) !== null && _b !== void 0 ? _b : (enumType.underlyingType = DefaultUnderlyingType);
        const enumName = enumType.name;
        const underlyingType = enumType.underlyingType.split('.').pop();
        const dataType = Utilities.getDataType(underlyingType);
        const isFlags = enumType.isFlags.toLowerCase() === TrueValue;
        const enumValue = new EdmEnum({
            isFlags: isFlags,
            name: enumName,
            underlyingDataType: dataType
        });
        enumType.member.forEach(member => this.adaptEnumMember(enumValue, member));
        DataType[enumName] = DataType.addSymbol(enumValue);
    }
    adaptEnumMember(enumValue, member) {
        enumValue.addSymbol({
            name: member.name,
            displayName: member.displayName,
            rawValue: member.value
        });
    }
}

var metadataAdapters = /*#__PURE__*/Object.freeze({
  __proto__: null,
  NavigationAdapter: NavigationAdapter,
  AnnotationAdapter: AnnotationAdapter,
  EnumTypeAdapter: EnumTypeAdapter
});

class ODataError extends Error {
    constructor(message) {
        super(message);
        this.toString = () => {
            return `${this.name}: ${this.message}`;
        };
        Object.setPrototypeOf(this, ODataError.prototype);
    }
}

class ProxyDataService {
}
Object.setPrototypeOf(ProxyDataService.prototype, config.getAdapter('dataService', 'WebApi').prototype);
class OData4DataService extends ProxyDataService {
    constructor() {
        super();
        this.innerAdapter = config.getAdapterInstance('dataService', 'WebApi');
        this.actions = [];
        this.name = OData4DataService.BreezeAdapterName;
        this.headers = {
            'OData-Version': '4.0'
        };
        this.metadataAcceptHeader = 'application/json;odata.metadata=full';
        this.changeRequestInterceptor = this.innerAdapter.changeRequestInterceptor;
    }
    static register() {
        config.registerAdapter('dataService', OData4DataService);
    }
    _catchNoConnectionError(err) {
        return this.innerAdapter._catchNoConnectionError(err);
    }
    _createChangeRequestInterceptor(saveContext, saveBundle) {
        return this.innerAdapter._createChangeRequestInterceptor(saveContext, saveBundle);
    }
    checkForRecomposition(interfaceInitializedArgs) {
        this.innerAdapter.checkForRecomposition(interfaceInitializedArgs);
    }
    _prepareSaveBundle(saveContext, saveBundle) {
        return this.innerAdapter._prepareSaveBundle(saveContext, saveBundle);
    }
    _prepareSaveResult(saveContext, saveResult) {
        return this.innerAdapter._prepareSaveResult(saveContext, saveResult);
    }
    initialize() {
        this.jsonResultsAdapter = JsonResultsAdapterFactory.create();
    }
    getAbsoluteUrl(dataService, url) {
        const serviceName = dataService.qualifyUrl('');
        let base = core.stringStartsWith(url, serviceName) ? '' : serviceName;
        if (typeof window !== 'undefined' && !serviceName.startsWith('//') && !serviceName.startsWith('http')) {
            base = `${location.origin}${core.stringStartsWith(serviceName, '/') ? '' : '/'}${base}`;
        }
        return base + url;
    }
    fetchMetadata(metadataStore, dataService) {
        const serviceName = dataService.serviceName;
        const url = this.getAbsoluteUrl(dataService, '$metadata');
        return new Promise((resolve, reject) => {
            oData.read({
                requestUri: url,
                headers: Object.assign({}, this.headers, { Accept: this.metadataAcceptHeader })
            }, (data) => {
                if (!(data === null || data === void 0 ? void 0 : data.dataServices)) {
                    const error = new Error(`Metadata query failed for: ${url}`);
                    return reject(error);
                }
                this.metadata = data;
                const csdlMetadata = this.metadata.dataServices;
                Utilities.adaptMetadata(this.metadata);
                if (!metadataStore.hasMetadataFor(serviceName)) {
                    try {
                        metadataStore.importMetadata(csdlMetadata);
                    }
                    catch (e) {
                        reject(new Error(`Metadata query failed for ${url}; Unable to process returned metadata: ${e.message}`));
                    }
                    metadataStore.addDataService(dataService);
                }
                this.actions = Utilities.getActions(this.metadata, metadataStore);
                resolve(csdlMetadata);
            }, (error) => {
                const err = this.createError(error, url);
                err.message = `Metadata query failed for: ${url}; ${err.message}`;
                reject(err);
            }, oData.metadataHandler, this.httpClient);
        });
    }
    executeQuery(mappingContext) {
        const query = mappingContext.query;
        const request = this.getRequest(mappingContext);
        return new Promise((resolve, reject) => {
            oData.request(request, (data, response) => {
                let inlineCount;
                let results;
                if (data) {
                    inlineCount = Number(data['@odata.count']);
                    results = data.value;
                }
                resolve({ results: results, query: query, inlineCount: inlineCount, httpResponse: response });
            }, (error) => {
                const err = this.createError(error, request.requestUri);
                reject(err);
            }, null, this.httpClient);
        });
    }
    saveChanges(saveContext, saveBundle) {
        saveContext.adapter = this;
        saveContext.routePrefix = this.getAbsoluteUrl(saveContext.dataService, '');
        const url = `${saveContext.routePrefix}$batch`;
        const requestData = this.createChangeRequests(saveContext, saveBundle);
        const tempKeys = saveContext.tempKeys;
        const contentKeys = saveContext.contentKeys;
        return new Promise((resolve, reject) => {
            oData.request({
                method: 'POST',
                requestUri: url,
                headers: Object.assign({}, this.headers),
                data: requestData
            }, (data) => {
                const entities = [];
                const keyMappings = [];
                const saveResult = { entities: entities, keyMappings: keyMappings, deletedKeys: null, XHR: null };
                data.__batchResponses.forEach((br) => {
                    br.__changeResponses.forEach((cr, index) => {
                        var _a;
                        const chResponse = cr.response || cr;
                        const statusCode = chResponse.statusCode;
                        if (!statusCode || Number(statusCode) >= 400) {
                            const err = this.createError(cr, url);
                            reject(err);
                            return;
                        }
                        const contentId = Number((_a = (chResponse.headers || {})['Content-ID']) !== null && _a !== void 0 ? _a : 0);
                        const origEntity = contentKeys[contentId];
                        const rawEntity = chResponse.data;
                        if (rawEntity) {
                            const tempKey = tempKeys[contentId];
                            if (tempKey) {
                                const entityType = tempKey.entityType;
                                if (entityType.autoGeneratedKeyType !== AutoGeneratedKeyType.None) {
                                    const tempValue = tempKey.values[0];
                                    const realKey = entityType.getEntityKeyFromRawEntity(rawEntity, DataProperty.getRawValueFromServer);
                                    const keyMapping = {
                                        entityTypeName: entityType.name,
                                        tempValue: tempValue,
                                        realValue: realKey.values[0]
                                    };
                                    keyMappings.push(keyMapping);
                                }
                            }
                            entities.push(rawEntity);
                        }
                        else if (origEntity) {
                            entities.push(origEntity);
                        }
                    });
                });
                resolve(saveResult);
            }, err => {
                const error = this.createError(err, url);
                reject(error);
            }, oData.batch.batchHandler, this.httpClient, this.metadata);
        });
    }
    createChangeRequests(saveContext, saveBundle) {
        const changeRequestInterceptor = this._createChangeRequestInterceptor(saveContext, saveBundle);
        const changeRequests = [];
        const tempKeys = [];
        const contentKeys = [];
        const entityManager = saveContext.entityManager;
        const helper = entityManager.helper;
        let contentId = 1;
        const routePrefix = saveContext.routePrefix;
        saveBundle.entities.forEach((entity, index) => {
            const aspect = entity.entityAspect;
            let request = {
                headers: Object.assign({
                    'Content-ID': contentId.toString(),
                    'Content-Type': 'application/json;IEEE754Compatible=true'
                }, this.headers),
                requestUri: null,
                method: null
            };
            contentKeys[contentId] = entity;
            if (aspect.entityState.isAdded()) {
                request.requestUri = routePrefix + entity.entityType.defaultResourceName;
                request.method = 'POST';
                request.data = helper.unwrapInstance(entity, this.transformValue);
                tempKeys[contentId] = aspect.getKey();
            }
            else if (aspect.entityState.isModified()) {
                this.updateDeleteMergeRequest(request, aspect, routePrefix);
                request.method = 'PATCH';
                request.data = helper.unwrapChangedValues(entity, entityManager.metadataStore, this.transformValue);
            }
            else if (aspect.entityState.isDeleted()) {
                this.updateDeleteMergeRequest(request, aspect, routePrefix);
                request.method = 'DELETE';
            }
            else {
                return;
            }
            request = changeRequestInterceptor.getRequest(request, entity, index);
            changeRequests.push(request);
            contentId++;
        });
        saveContext.contentKeys = contentKeys;
        saveContext.tempKeys = tempKeys;
        changeRequestInterceptor.done(changeRequests);
        const changeRequestSet = [
            {
                __changeRequests: changeRequests
            }
        ];
        const batchRequest = {
            __batchRequests: changeRequestSet
        };
        return batchRequest;
    }
    getRequest(mappingContext) {
        var _a;
        const query = mappingContext.query;
        let method = 'GET';
        let request = {
            method: method,
            requestUri: this.getUrl(mappingContext),
            headers: Object.assign({}, this.headers)
        };
        if (!(query === null || query === void 0 ? void 0 : query.parameters)) {
            return request;
        }
        method = query.parameters['$method'] || method;
        delete query.parameters['$method'];
        if (method === 'GET') {
            request = Object.assign({}, request, { requestUri: this.addQueryString(request.requestUri, query.parameters) });
        }
        else {
            const data = (_a = this.getData(mappingContext, query.parameters['$data'])) !== null && _a !== void 0 ? _a : query.parameters;
            request = Object.assign({}, request, { method: method, data: data });
        }
        return request;
    }
    getData(mappingContext, data) {
        if (!data) {
            return null;
        }
        if (!this.areValidPropertyNames(mappingContext.metadataStore, data)) {
            return data;
        }
        const helper = mappingContext.entityManager.helper;
        if (data.entityType || data.complexType) {
            return helper.unwrapInstance(data, null);
        }
        const invokeConfig = this.getInvokableConfig(mappingContext.query.resourceName);
        const actionEntry = this.actions.find(e => e.config === invokeConfig);
        if (!actionEntry) {
            return data;
        }
        const paramStartIndex = Number((invokeConfig.isBound === 'true'));
        const param = invokeConfig.parameter.find((p, idx) => {
            const isParameter = idx >= paramStartIndex;
            const isEdmType = p.type.startsWith('Edm.');
            const result = isParameter && !isEdmType;
            return result;
        });
        if (!param) {
            return data;
        }
        const edmType = Utilities.getEdmTypeFromTypeName(this.metadata, param.type);
        if (!edmType) {
            return data;
        }
        const structuralType = Utilities.adaptStructuralType(mappingContext.metadataStore, edmType);
        if (structuralType instanceof EntityType) {
            data = structuralType.createEntity(data);
            return helper.unwrapInstance(data, null);
        }
        else if (structuralType instanceof ComplexType) {
            data = structuralType.createInstance(data);
            return helper.unwrapInstance(data, null);
        }
        return data;
    }
    areValidPropertyNames(metadataStore, data) {
        const props = Object.keys(data);
        const result = props.every(p => {
            const sp = metadataStore.namingConvention.clientPropertyNameToServer(p);
            const cp = metadataStore.namingConvention.serverPropertyNameToClient(sp);
            return p === cp;
        });
        return result;
    }
    getInvokableConfig(url) {
        if (!url) {
            return null;
        }
        const urlParts = url.split('/');
        const invokableName = urlParts.pop().replace(/\([^\)]*\)/, '');
        const actionConfig = oData.utils.lookupAction(invokableName, this.metadata);
        const functionConfig = oData.utils.lookupFunction(invokableName, this.metadata);
        return actionConfig || functionConfig;
    }
    getUrl(mappingContext) {
        const url = this.getAbsoluteUrl(mappingContext.dataService, mappingContext.getUrl());
        return url;
    }
    addQueryString(url, parameters) {
        const queryString = this.toQueryString(parameters);
        if (!queryString) {
            return url;
        }
        const sep = url.indexOf('?') < 0 ? '?' : '&';
        url += sep + queryString;
        return url;
    }
    transformValue(prop, val) {
        if (prop.isUnmapped) {
            return undefined;
        }
        if (prop.dataType.quoteJsonOData) {
            val = val != null ? val.toString() : val;
        }
        return val;
    }
    updateDeleteMergeRequest(request, aspect, routePrefix) {
        if (!aspect.extraMetadata) {
            aspect.extraMetadata = {};
        }
        const extraMetadata = aspect.extraMetadata;
        if (extraMetadata['etag']) {
            request.headers['If-Match'] = extraMetadata['etag'];
        }
        if (!extraMetadata['uriKey']) {
            extraMetadata['uriKey'] = this.getUriKey(aspect);
        }
        const uriKey = extraMetadata['uriKey'];
        request.requestUri =
            uriKey.indexOf('//') > 0 ? uriKey : routePrefix + uriKey;
    }
    getUriKey(aspect) {
        const entityType = aspect.entity.entityType;
        const resourceName = entityType.defaultResourceName;
        const kps = entityType.keyProperties;
        const uriKeyValue = kps.length === 1
            ? this.fmtProperty(kps[0], aspect)
            : kps.map(kp => {
                return `${kp.nameOnServer}=${this.fmtProperty(kp, aspect)}`;
            });
        const uriKey = `${resourceName}(${uriKeyValue})`;
        return uriKey;
    }
    fmtProperty(prop, aspect) {
        return prop.dataType.fmtOData(aspect.getPropertyValue(prop.name));
    }
    createError(error, url) {
        const result = new ODataError();
        const response = error && error.response;
        result.message = error.message || error;
        result.statusText = error.message || error;
        if (!response) {
            return result;
        }
        if (response.statusCode !== '200') {
            result.message = response.statusText;
            result.statusText = response.statusText;
            result.status = Number(response.statusCode);
        }
        if (url) {
            result.url = url;
        }
        result.body = response.body;
        if (response.body) {
            let nextErr;
            try {
                let body = JSON.parse(response.body);
                result.body = body;
                if (body['odata.error']) {
                    body = body['odata.error'];
                }
                let msg = '';
                do {
                    nextErr = body.error || body.innererror;
                    if (!nextErr) {
                        msg = msg + this.getMessage(body);
                    }
                    nextErr = nextErr || body.internalexception;
                    body = nextErr || body;
                } while (nextErr);
                if (msg.length > 0) {
                    result.message = msg;
                }
            }
            catch (e) { }
        }
        this._catchNoConnectionError(result);
        return result;
    }
    getMessage(body) {
        const messageKey = Object.keys(body)
            .find(k => k.toLowerCase() === 'message');
        if (!messageKey) {
            return '';
        }
        const msg = body[messageKey];
        return `${(typeof msg === 'string' ? msg : msg.value)}; `;
    }
    toQueryString(payload) {
        const keys = Object.keys(payload);
        if (!keys.length) {
            return null;
        }
        const result = keys
            .map(key => {
            return `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`;
        })
            .join('&');
        return result;
    }
}
OData4DataService.BreezeAdapterName = 'OData4';

const DefaultOptions = {
    allowManyToManyRelationships: false,
    annotationDecorators: [],
    dataTypeSetups: [],
    foreignKeyConventions: [],
    inferNavigationPropertyPartner: true,
    inferReferentialConstraints: true,
    initializeAdapters: true,
    metadataAdapters: []
};

const ExpandParamsKey = 'expandParams';
class OData4EntityQuery extends EntityQuery {
    expand(propertyPaths, subQuery) {
        if (propertyPaths instanceof Array || !subQuery) {
            return super.expand(propertyPaths);
        }
        this.parameters[ExpandParamsKey] = this.parameters[ExpandParamsKey] || {};
        this.parameters[ExpandParamsKey][propertyPaths] = subQuery;
        return this;
    }
}

class OData4PredicateVisitor {
    static initialize() {
        const visitor = new OData4PredicateVisitor();
        Predicate.prototype.toODataFragment = function (context) {
            return this.visit(context, visitor);
        };
    }
    passthruPredicate() {
        return this.value;
    }
    unaryPredicate(context) {
        const predVal = this.pred.visit(context);
        const op = this.op.key;
        return `${op} (${predVal})`;
    }
    binaryPredicate(context) {
        let expr1Val = this.expr1.visit(context);
        const expr2Val = this.expr2.visit(context);
        const prefix = context.prefix;
        if (prefix) {
            expr1Val = `${prefix}/${expr1Val}`;
        }
        const op = this.op.key;
        if (op === 'in') {
            const result = expr2Val
                .map(v => `(${expr1Val} eq ${v})`)
                .join(' or ');
            return result;
        }
        else if (this.op.isFunction) {
            return `${op}(${expr1Val},${expr2Val})`;
        }
        else {
            return `${expr1Val} ${op} ${expr2Val}`;
        }
    }
    andOrPredicate(context) {
        const op = this.op.key;
        const result = this.preds
            .map(pred => `(${pred.visit(context)})`)
            .join(` ${op} `);
        return result;
    }
    anyAllPredicate(context) {
        const op = this.op.key;
        let exprVal = this.expr.visit(context);
        if (!this.pred.op) {
            return `${exprVal}/${op}()`;
        }
        let prefix = context.prefix;
        let idx = 1;
        if (prefix) {
            exprVal = `${prefix}/${exprVal}`;
            idx = Number(prefix.substring(1)) + 1;
        }
        prefix = `x${idx}`;
        const newContext = Object.assign({}, context, { entityType: this.expr.dataType, prefix: prefix });
        const newPredVal = this.pred.visit(newContext);
        return `${exprVal}/${op}(${prefix}: ${newPredVal})`;
    }
    litExpr() {
        if (Array.isArray(this.value)) {
            return this.value
                .map(v => this.dataType.fmtOData(v));
        }
        else {
            return this.dataType.fmtOData(this.value);
        }
    }
    propExpr(context) {
        const entityType = context.entityType;
        return entityType
            ? entityType.clientPropertyPathToServer(this.propertyPath, '/')
            : this.propertyPath;
    }
    fnExpr(context) {
        const exprVals = this.exprs
            .map(expr => expr.visit(context));
        return `${this.fnName}(${exprVals})`;
    }
}

class OData4UriBuilder {
    constructor() {
        this.name = OData4UriBuilder.BreezeAdapterName;
    }
    static register() {
        config.registerAdapter('uriBuilder', OData4UriBuilder);
    }
    initialize() {
        OData4PredicateVisitor.initialize();
    }
    buildUri(entityQuery, metadataStore) {
        const queryOptions = this.buildQueryOptions({ expand: [] }, entityQuery, metadataStore);
        const qoText = this.toQueryOptionsString(queryOptions);
        entityQuery.resourceName = this.getResource(entityQuery);
        return entityQuery.resourceName + qoText;
    }
    buildQueryOptions(queryOptions, entityQuery, metadataStore) {
        let entityType = entityQuery._getFromEntityType(metadataStore, false);
        if (!entityType) {
            entityType = new EntityType(metadataStore);
        }
        if (entityQuery.inlineCountEnabled) {
            queryOptions.count = true;
        }
        if (entityQuery.skipCount) {
            queryOptions.skip = entityQuery.skipCount;
        }
        if (entityQuery.takeCount != null) {
            queryOptions.top = entityQuery.takeCount;
        }
        this.addExpandOption(entityType, queryOptions, entityQuery.expandClause);
        this.addSelectOption(entityType, queryOptions, entityQuery.selectClause);
        if (entityQuery.parameters[ExpandParamsKey]) {
            this.addExpandOptionsFromSubqueries(entityType, queryOptions, entityQuery.parameters[ExpandParamsKey]);
            delete entityQuery.parameters[ExpandParamsKey];
        }
        queryOptions.filter = this.toWhereODataFragment(entityType, entityQuery.wherePredicate);
        queryOptions.orderby = this.toOrderByODataFragment(entityType, entityQuery.orderByClause);
        return queryOptions;
    }
    getResource(entityQuery) {
        let resource = entityQuery.resourceName;
        if (!core.isEmpty(entityQuery.parameters)) {
            resource = this.formatString(resource, entityQuery.parameters);
        }
        return resource;
    }
    getQueryOptions(rootOptions, propertyPath) {
        const path = [].concat(propertyPath);
        const rootProperty = path.shift().nameOnServer;
        if (!path.length) {
            return rootOptions;
        }
        let nextExpand = rootOptions.expand.find(e => e.name === rootProperty);
        if (!nextExpand) {
            nextExpand = { name: rootProperty, expand: [] };
            rootOptions.expand.push(nextExpand);
        }
        return this.getQueryOptions(nextExpand, path);
    }
    addExpandOption(entityType, queryOptions, expandClause) {
        if (!expandClause) {
            return;
        }
        expandClause.propertyPaths.forEach(pp => {
            const props = entityType.getPropertiesOnPath(pp, false, true);
            const workingOptions = this.getQueryOptions(queryOptions, props);
            const property = props.pop().nameOnServer;
            if (workingOptions.expand.find(e => e.name === property)) {
                return;
            }
            const expandOptions = { name: property, expand: [] };
            workingOptions.expand.push(expandOptions);
        });
    }
    addExpandOptionsFromSubqueries(entityType, queryOptions, subqueries) {
        Object.entries(subqueries).forEach(([key, value]) => {
            const props = entityType.getPropertiesOnPath(key, false, true);
            const workingOptions = this.getQueryOptions(queryOptions, props);
            const property = props.pop().nameOnServer;
            let currentExpand = workingOptions.expand.find(e => e.name === property);
            if (!currentExpand) {
                currentExpand = { name: property, expand: [] };
                workingOptions.expand.push(currentExpand);
            }
            this.buildQueryOptions(currentExpand, value, entityType.metadataStore);
        });
    }
    addSelectOption(entityType, queryOptions, selectClause) {
        if (!selectClause) {
            return;
        }
        selectClause.validate(entityType);
        selectClause.propertyPaths.forEach(pp => {
            const props = entityType.getPropertiesOnPath(pp, false, true);
            const workingOptions = this.getQueryOptions(queryOptions, props);
            workingOptions.select = workingOptions.select || [];
            const property = props.pop().nameOnServer;
            if (workingOptions.select.find(p => p === property)) {
                return;
            }
            workingOptions.select.push(property);
        });
    }
    toWhereODataFragment(entityType, wherePredicate) {
        if (!wherePredicate) {
            return undefined;
        }
        return wherePredicate.toODataFragment({ entityType: entityType });
    }
    toOrderByODataFragment(entityType, orderByClause) {
        if (!orderByClause) {
            return undefined;
        }
        orderByClause.validate(entityType);
        const orderBy = orderByClause.items.map(item => {
            const propertyPath = entityType.clientPropertyPathToServer(item.propertyPath, '/');
            const direction = item.isDesc ? ' desc' : '';
            return propertyPath + direction;
        });
        return orderBy.toString();
    }
    toQueryOptionsString(queryOptions) {
        let qoStrings = [];
        for (const qoName of Object.getOwnPropertyNames(queryOptions)) {
            const qoValue = queryOptions[qoName];
            if (qoValue !== undefined) {
                if (qoName === 'expand') {
                    qoStrings.push(this.getExpandString(qoValue));
                }
                else if (qoName === 'select') {
                    qoStrings.push(this.getSelectString(qoValue));
                }
                else {
                    qoStrings.push(`$${qoName}=${encodeURIComponent(qoValue)}`);
                }
            }
        }
        qoStrings = qoStrings.filter(s => !!s);
        if (qoStrings.length > 0) {
            return '?' + qoStrings.join('&');
        }
        else {
            return '';
        }
    }
    getSelectString(select) {
        const key = '$select';
        if (!(select && select.length)) {
            return null;
        }
        return `${key}=${select}`;
    }
    getFilterString(filter) {
        const key = '$filter';
        if (!filter) {
            return null;
        }
        return `${key}=${encodeURIComponent(filter)}`;
    }
    getExpandString(options) {
        const key = '$expand';
        const expandStrings = options.map(option => {
            const subOptions = [
                this.getFilterString(option.filter),
                this.getSelectString(option.select),
                this.getExpandString(option.expand)
            ].filter(s => !!s);
            if (!subOptions.length) {
                return option.name;
            }
            return `${option.name}(${subOptions.join(';')})`;
        });
        const result = expandStrings.length ? `${key}=${expandStrings}` : null;
        return result;
    }
    formatString(format, params) {
        const props = Object.keys(params);
        const args = props.map(prop => ({ key: prop, value: params[prop] }));
        const result = args.reduce((formattedString, arg, index) => {
            const key = arg.key;
            let value = arg.value;
            if (value == null) {
                value = '';
            }
            const reg = new RegExp(`\\{${key}\\}`, 'gm');
            const newString = formattedString.replace(reg, value);
            if (newString !== formattedString) {
                delete params[key];
            }
            return newString;
        }, format);
        return result;
    }
}
OData4UriBuilder.BreezeAdapterName = 'OData4';

class BaseDataTypeSetup {
    constructor() {
        this.addSymbol = null;
        this.fmtOData = null;
    }
    handleInvalidValue(val) {
        throw new Error(`${val} is not a valid ${this.name}`);
    }
    register() {
        if (!!this.addSymbol) {
            this.addSymbol();
        }
        if (!!this.fmtOData) {
            DataType[this.name].fmtOData = this.fmtOData;
        }
    }
}

const DateRegex = /(-\d)?\d{4}-\d{2}-\d{2}/;
class DateDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = 'Date';
        this.parseValue = (val) => {
            if (!val) {
                return null;
            }
            let result = val;
            if (typeof val === 'string') {
                const matchResult = DateRegex.exec(val);
                if (matchResult) {
                    return matchResult[0];
                }
            }
            const dateVal = val instanceof Date ? val : new Date(`${val} `);
            if (isNaN(dateVal)) {
                this.handleInvalidValue(val);
            }
            result = dateVal.toISOString().split('T')[0];
            return result;
        };
        this.addSymbol = () => {
            const result = DataType.addSymbol({
                defaultValue: '0000-01-01',
                name: this.name,
                isDate: true,
                fmtOData: this.parseValue,
                parseRawValue: this.parseValue
            });
            DataType[this.name] = result;
            return result;
        };
    }
}

class DateTimeDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = nameof('DateTime');
        this.fmtOData = (val) => {
            if (!val) {
                return null;
            }
            try {
                return val.toISOString();
            }
            catch (e) {
                this.handleInvalidValue(val);
            }
        };
    }
}

class DateTimeOffsetDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = nameof('DateTimeOffset');
        this.fmtOData = (val) => {
            if (!val) {
                return null;
            }
            try {
                return val.toISOString();
            }
            catch (e) {
                this.handleInvalidValue(val);
            }
        };
    }
}

class DurationDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = 'Duration';
        this.fmtOData = (val) => {
            if (!val) {
                return null;
            }
            if (!core.isDuration(val)) {
                throw new Error(`${val} is not a valid ISO 8601 duration`);
            }
            return val;
        };
        this.addSymbol = () => {
            const result = DataType.addSymbol(Object.assign({}, DataType.Time, {
                name: this.name,
                parseRawValue: DataType.parseTimeFromServer
            }));
            DataType[this.name] = result;
            return result;
        };
    }
}

class GeographyPointDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = 'GeographyPoint';
        this.addSymbol = () => {
            const result = DataType.addSymbol({
                defaultValue: [0, 0],
                fmtOData: JSON.stringify,
                name: this.name,
                parse: DataType.String.parse,
                validatorCtor: Validator.string
            });
            DataType[this.name] = result;
            return result;
        };
    }
}

class GuidDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = nameof('Guid');
        this.fmtOData = (val) => {
            if (!val) {
                return null;
            }
            if (!core.isGuid(val)) {
                this.handleInvalidValue(val);
            }
            return val;
        };
    }
}

class NumericDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.fmtOData = (val) => {
            if (typeof val === 'undefined' || val === null) {
                return null;
            }
            if (typeof val === 'string') {
                val = parseFloat(val);
            }
            return val;
        };
    }
    register() {
        DataType.Decimal.fmtOData = this.fmtOData;
        DataType.Double.fmtOData = this.fmtOData;
        DataType.Int64.fmtOData = this.fmtOData;
    }
}

class StreamDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = 'Stream';
        this.addSymbol = () => {
            const result = DataType.addSymbol({
                defaultValue: '',
                fmtOData: DataType.String.fmtOData,
                name: this.name,
                parse: DataType.String.parse,
                validatorCtor: Validator.string
            });
            DataType[this.name] = result;
            return result;
        };
    }
}

class TimeDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = nameof('Time');
        this.fmtOData = (val) => {
            if (!val) {
                return null;
            }
            if (!core.isDuration(val)) {
                throw new Error(`${val} is not a valid ISO 8601 duration`);
            }
            return val;
        };
    }
}

class TimeOfDayDataTypeSetup extends BaseDataTypeSetup {
    constructor() {
        super(...arguments);
        this.name = 'TimeOfDay';
        this.addSymbol = () => {
            const result = DataType.addSymbol({
                defaultValue: '00:00',
                fmtOData: DataType.String.fmtOData,
                parse: DataType.String.parse,
                name: this.name,
                validatorCtor: Validator.string
            });
            DataType[this.name] = result;
            return result;
        };
    }
}

var datatypeSetups = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DateDataType: DateDataTypeSetup,
  DateTimeDataType: DateTimeDataTypeSetup,
  DateTimeOffsetDataType: DateTimeOffsetDataTypeSetup,
  DurationDataType: DurationDataTypeSetup,
  GeographyPointDataType: GeographyPointDataTypeSetup,
  GuidDataType: GuidDataTypeSetup,
  NumericDataType: NumericDataTypeSetup,
  StreamDataType: StreamDataTypeSetup,
  TimeDataType: TimeDataTypeSetup,
  TimeOfDayDataType: TimeOfDayDataTypeSetup
});

const CustomTerm = 'Custom';
class CustomDecorator {
    canDecorate(annotation) {
        return /.\.Custom\./.test(annotation.term);
    }
    decorate(expression, annotation) {
        const keys = Object.keys(annotation);
        core.arrayRemoveItem(keys, 'term', false);
        const valueKey = keys[0];
        const value = annotation[valueKey];
        const termParts = annotation.term.split('.');
        const startIndex = termParts.indexOf(CustomTerm);
        const customPath = termParts.slice(startIndex + 1);
        if (!customPath.length) {
            return;
        }
        const dataType = Utilities.getDataType(valueKey);
        const customValue = Utilities.parseValue(dataType, value);
        expression.custom = expression.custom instanceof Object ? expression.custom : {};
        customPath.reduce((prev, curr, index, arr) => {
            if (index < arr.length - 1) {
                prev[curr] = prev[curr] instanceof Object ? prev[curr] : {};
                return prev[curr];
            }
            else {
                prev[curr] = customValue;
                return prev;
            }
        }, expression.custom);
    }
}

class DescriptionDecorator {
    constructor() {
        this.terms = ['Org.OData.Display.V1.Description', 'Org.OData.Core.V1.Description'];
    }
    canDecorate(annotation) {
        return this.terms.includes(annotation.term);
    }
    decorate(expression, annotation) {
        const value = annotation.string;
        expression.description = typeof value === 'string' ? value : value.text;
    }
}

class DisplayNameDecorator {
    canDecorate(annotation) {
        return annotation.term === 'Org.OData.Display.V1.DisplayName' || /.\.DisplayName$/.test(annotation.term);
    }
    decorate(expression, annotation) {
        const value = annotation.string;
        expression.displayName = typeof value === 'string' ? value : value.text;
    }
}

const PublicationTerm = 'Org.OData.Publication.V1.';
class PublicationDecorator {
    canDecorate(annotation) {
        return annotation.term.startsWith(PublicationTerm);
    }
    decorate(expression, annotation) {
        const keys = Object.keys(annotation);
        core.arrayRemoveItem(keys, 'term', false);
        const valueKey = keys[0];
        const value = annotation[valueKey];
        const propName = annotation.term.replace(PublicationTerm, '');
        if (propName === '') {
            return;
        }
        const dataType = Utilities.getDataType(valueKey);
        const parsedValue = Utilities.parseValue(dataType, value);
        expression.publication = expression.publication instanceof Object ? expression.publication : {};
        expression.publication[propName] = parsedValue;
    }
}

class StoreGeneratedPatternDecorator {
    canDecorate(annotation) {
        return /.\.StoreGeneratedPattern$/.test(annotation.term);
    }
    decorate(expression, annotation) {
        const value = annotation.string;
        expression.StoreGeneratedPattern = typeof value === 'string' ? value : value.text;
    }
}

const ValidatorTerm = 'Validator';
class ValidatorDecorator {
    canDecorate(annotation) {
        return annotation.term.indexOf(`.${ValidatorTerm}`) > -1;
    }
    decorate(expression, annotation) {
        expression.validators = expression.validators || [];
        const keys = Object.keys(annotation);
        core.arrayRemoveItem(keys, 'term', false);
        const valueKey = keys[0];
        const value = annotation[valueKey];
        const nameAndProp = annotation.term.replace(/^.*Validator\.?/, '').split('.');
        const name = nameAndProp.shift();
        const prop = nameAndProp.shift();
        if (!name) {
            return;
        }
        let validator = expression.validators.find((val) => {
            return val.name === name;
        });
        if (!validator) {
            validator = { name: name };
            expression.validators.push(validator);
        }
        if (!prop) {
            return;
        }
        const dataType = Utilities.getDataType(valueKey);
        validator[prop] = Utilities.parseValue(dataType, value);
    }
}

var annotationDecorators = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CustomDecorator: CustomDecorator,
  DescriptionDecorator: DescriptionDecorator,
  DisplayNameDecorator: DisplayNameDecorator,
  PublicationDecorator: PublicationDecorator,
  StoreGeneratedPatternDecorator: StoreGeneratedPatternDecorator,
  ValidatorDecorator: ValidatorDecorator
});

class BreezeOData4 {
    static configure(options) {
        const opts = Object.assign({}, DefaultOptions, options || {});
        if (!this.isConfigured) {
            OData4UriBuilder.register();
            OData4DataService.register();
            NavigationAdapter.allowManyToMany = opts.allowManyToManyRelationships;
            NavigationAdapter.foreignKeyConventions = [
                ...opts.foreignKeyConventions,
                ...NavigationAdapter.foreignKeyConventions
            ];
            NavigationAdapter.inferPartner = opts.inferNavigationPropertyPartner;
            NavigationAdapter.inferConstraints = opts.inferReferentialConstraints;
            BreezeOData4.registerClasses(opts);
            BreezeOData4.setupDataTypes();
            this.isConfigured = true;
        }
        if (opts.initializeAdapters) {
            config.initializeAdapterInstance('uriBuilder', OData4UriBuilder.BreezeAdapterName, true);
            config.initializeAdapterInstance('dataService', OData4DataService.BreezeAdapterName, true);
        }
    }
    static reset() {
        this.isConfigured = false;
    }
    static registerClasses(options) {
        ClassRegistry.AnnotationDecorators.add(...Object.values(annotationDecorators), ...options.annotationDecorators);
        ClassRegistry.DataTypeSetups.add(...Object.values(datatypeSetups), ...options.dataTypeSetups);
        ClassRegistry.MetadataAdapters.add(...Object.values(metadataAdapters), ...options.metadataAdapters);
    }
    static setupDataTypes() {
        const dataTypeSetups = ClassRegistry.DataTypeSetups.get();
        dataTypeSetups.forEach(s => s && s.register());
    }
}
BreezeOData4.isConfigured = false;

class ODataHttpClient {
    constructor() {
        this.formatQueryString = oData.net.defaultHttpClient.formatQueryString;
    }
    request(request, success, error) {
        return oData.net.defaultHttpClient.request(request, success, error);
    }
}

export { BaseDataTypeSetup, BreezeOData4, ClassRegistry, ClassRepository, EdmEnum, JsonResultsAdapterFactory, OData4DataService, OData4EntityQuery, OData4PredicateVisitor, OData4UriBuilder, ODataHttpClient, Utilities };
//# sourceMappingURL=breeze-odata4.es5.js.map
